import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDynamicWidgets, useInstantSearch } from 'react-instantsearch';

import {
  filterFacetsWithValues,
  makeFacetTransformItems,
} from '@utils/algolia/facetOrdering';
import { AlgoliaFacets } from '@utils/algolia/knownFacetNames';

import { normalizeFacetKey, useRefinementCounts } from './useRefinementCounts';

/** How many facets surface as pills before the rest go behind "View filters" (ED-312). */
const PILL_FACET_LIMIT = 8;

/**
 * When the index exposes no `renderingContent.facetOrdering`,
 * `useDynamicWidgets` returns an empty list. Fall back to the facets the
 * legacy sidebar declares explicitly so the filter UI never renders blank.
 */
const getFallbackFacets = (priceAttribute: string) => [
  'categories.lvl0',
  AlgoliaFacets.Gender,
  AlgoliaFacets.Brand,
  priceAttribute,
  AlgoliaFacets.ColourVariants,
  AlgoliaFacets.ReviewScoreFlattened,
  'size',
  AlgoliaFacets.ClothingSizeKids,
  AlgoliaFacets.ClothingSizeMens,
  AlgoliaFacets.ClothingSizeWomens,
];

type FiltersContextValue = {
  /** Every facet for the current PLP, in the index's facetOrdering order. */
  orderedFacets: string[];
  /** The first facets shown as dropdown pills in the filter bar. */
  pillFacets: string[];
  isFiltersPanelOpen: boolean;
  /** Pass the invoking element so focus can return to it on close. */
  openFiltersPanel: (trigger?: HTMLElement | null) => void;
  closeFiltersPanel: () => void;
  /** Active refinement count for a facet attribute (alias-insensitive). */
  getRefinementCount: (attribute: string) => number;
  totalRefinementCount: number;
  locale?: string;
  priceAttribute: string;
};

const FiltersContext = createContext<FiltersContextValue | null>(null);

export const useFilters = () => {
  const context = useContext(FiltersContext);
  if (!context) {
    throw new Error('useFilters must be used within a FiltersProvider');
  }
  return context;
};

/**
 * Owns the redesigned PLP filter state (ED-320): the ordered facet list from
 * the index's facetOrdering (mounted once via useDynamicWidgets so the pill
 * bar and the all-filters panel agree), the first-8 pill slice, the open/close
 * state of the all-filters panel, and per-facet refinement counts.
 */
export const FiltersProvider = ({
  children,
  locale,
  categoryUrl,
  priceAttribute,
}: {
  children: ReactNode;
  locale?: string;
  categoryUrl: string;
  priceAttribute: string;
}) => {
  const transformItems = useMemo(
    () => makeFacetTransformItems({ categoryUrl, priceAttribute }),
    [categoryUrl, priceAttribute]
  );

  const { attributesToRender } = useDynamicWidgets({
    facets: ['*'],
    maxValuesPerFacet: 1000,
    transformItems,
  });

  const { results } = useInstantSearch();

  const { counts, total } = useRefinementCounts(priceAttribute);

  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
  const triggerRef = useRef<HTMLElement | null>(null);

  const openFiltersPanel = useCallback((trigger?: HTMLElement | null) => {
    triggerRef.current = trigger ?? null;
    setIsFiltersPanelOpen(true);
  }, []);

  const closeFiltersPanel = useCallback(() => {
    setIsFiltersPanelOpen(false);
    triggerRef.current?.focus();
    triggerRef.current = null;
  }, []);

  const getRefinementCount = useCallback(
    (attribute: string) =>
      counts.get(normalizeFacetKey(attribute, priceAttribute)) ?? 0,
    [counts, priceAttribute]
  );

  const value = useMemo(() => {
    const source =
      attributesToRender.length > 0
        ? attributesToRender
        : transformItems(getFallbackFacets(priceAttribute), { results });

    // The index's facetOrdering lists every hierarchical category level
    // (categories.lvl0…lvl7) as its own entry; they are one Category facet in
    // the UI, so keep only the first.
    let categoriesSeen = false;
    const dedupedFacets = source.filter((attribute) => {
      if (attribute.startsWith('categories.lvl')) {
        if (categoriesSeen) return false;
        categoriesSeen = true;
      }
      return true;
    });

    const orderedFacets = filterFacetsWithValues(dedupedFacets, {
      results,
      priceAttribute,
      hasRefinement: (attribute) => getRefinementCount(attribute) > 0,
    });

    return {
      orderedFacets,
      pillFacets: orderedFacets.slice(0, PILL_FACET_LIMIT),
      isFiltersPanelOpen,
      openFiltersPanel,
      closeFiltersPanel,
      getRefinementCount,
      totalRefinementCount: total,
      locale,
      priceAttribute,
    };
  }, [
    attributesToRender,
    transformItems,
    results,
    isFiltersPanelOpen,
    openFiltersPanel,
    closeFiltersPanel,
    getRefinementCount,
    total,
    locale,
    priceAttribute,
  ]);

  return (
    <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>
  );
};
